import type { BalloonShape, BalloonSkin } from './BalloonSkin.js';
import type { Item, State } from './BalloonSimulation.js';

export function draw(ctx: CanvasRenderingContext2D, state: State, skin: BalloonSkin): void {
  const { width, height } = state;
  const gradient = ctx.createLinearGradient(0, 0, 0, height);
  gradient.addColorStop(0, skin.background[0]);
  gradient.addColorStop(1, skin.background[1]);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);

  for (const item of state.items) {
    drawItem(ctx, item, skin.shape);
  }

  for (const p of state.particles) {
    ctx.globalAlpha = Math.max(p.life / p.maxLife, 0);
    ctx.fillStyle = p.color;
    ctx.beginPath();
    ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

function drawItem(ctx: CanvasRenderingContext2D, item: Item, shape: BalloonShape): void {
  switch (shape) {
    case 'balloon':
      drawBalloon(ctx, item);
      break;
    case 'star':
      drawStar(ctx, item);
      break;
    case 'bubble':
      drawBubble(ctx, item);
      break;
  }
}

function drawBalloon(ctx: CanvasRenderingContext2D, { x, y, r, color }: Item): void {
  const rx = r;
  const ry = r * 1.2;

  ctx.strokeStyle = 'rgba(60, 50, 80, 0.45)';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(x, y + ry + 6);
  ctx.bezierCurveTo(x - r * 0.3, y + ry + r * 0.8, x + r * 0.3, y + ry + r * 1.3, x - r * 0.1, y + ry + r * 2);
  ctx.stroke();

  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(x - 6, y + ry + 7);
  ctx.lineTo(x + 6, y + ry + 7);
  ctx.lineTo(x, y + ry - 2);
  ctx.closePath();
  ctx.fill();

  ctx.beginPath();
  ctx.ellipse(x, y, rx, ry, 0, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = 'rgba(255, 255, 255, 0.45)';
  ctx.beginPath();
  ctx.ellipse(x - rx * 0.35, y - ry * 0.4, rx * 0.18, ry * 0.28, -0.5, 0, Math.PI * 2);
  ctx.fill();
}

function drawStar(ctx: CanvasRenderingContext2D, { x, y, r, color }: Item): void {
  const outer = r * 1.15;
  const inner = outer * 0.48;
  ctx.fillStyle = color;
  ctx.beginPath();
  for (let i = 0; i < 10; i++) {
    const radius = i % 2 === 0 ? outer : inner;
    const angle = -Math.PI / 2 + (Math.PI * i) / 5;
    const px = x + Math.cos(angle) * radius;
    const py = y + Math.sin(angle) * radius;
    if (i === 0) {
      ctx.moveTo(px, py);
    } else {
      ctx.lineTo(px, py);
    }
  }
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
  ctx.beginPath();
  ctx.arc(x - outer * 0.2, y - outer * 0.2, outer * 0.14, 0, Math.PI * 2);
  ctx.fill();
}

function drawBubble(ctx: CanvasRenderingContext2D, { x, y, r, color }: Item): void {
  ctx.globalAlpha = 0.35;
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.globalAlpha = 1;

  ctx.strokeStyle = color;
  ctx.lineWidth = 2.5;
  ctx.stroke();

  ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
  ctx.beginPath();
  ctx.ellipse(x - r * 0.4, y - r * 0.4, r * 0.2, r * 0.12, -0.7, 0, Math.PI * 2);
  ctx.fill();
}
